// This creates a client. It will be used to fetch data.
// TODO: only create one client for the whole app
// TODO: move config to config folder

import { AxiosError } from 'axios';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  UseQueryOptions,
  UseMutationOptions,
  DefaultOptions,
} from '@tanstack/react-query';
import { refreshUserToken } from './auth';
import { captureClassifiedException } from './captureClassifiedException';
import { showUnexpectedMutationErrorToast } from './handleMutationError';

/**
 * Per-query/-mutation configuration attached via React Query's `meta`.
 * Declare error expectations once, on the query definition, and the central
 * `queryCache.onError` handler + `QueryErrorState` derive behavior from it.
 */
export interface AppQueryMeta extends Record<string, unknown> {
  /** `codeError` values treated as expected business outcomes (never reported to Sentry). */
  expectedCodeErrors?: string[];
  /**
   * Skip the central mutation error toast and Sentry capture — the caller handles
   * UX locally (e.g. worksheet save conflicts via `showWorksheetConflictToast`).
   */
  suppressGlobalMutationError?: boolean;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: AppQueryMeta;
    mutationMeta: AppQueryMeta;
  }
}

const queryConfig: DefaultOptions = {
  queries: {
    throwOnError: false,
    refetchOnWindowFocus: false,
    retry: true,
  },
  mutations: {
    onError: async (error) => {
      if ((error as AxiosError).response?.status === 401) {
        await refreshUserToken();
      }
    },
  },
};

/**
 * Central reporting for query failures. Fires exactly once per failure,
 * independent of whether any component renders the error state, so reporting
 * can no longer be missed on fast navigation or double-fire on remount.
 * `captureClassifiedException` drops expected errors internally.
 */
const queryCache = new QueryCache({
  onError: (error, query) => {
    captureClassifiedException(error, {
      source: 'query',
      layer: 'fetch',
      expectedCodeErrors: query.meta?.expectedCodeErrors,
    });
  },
});

/**
 * Central reporting + toast for mutation failures. Fires exactly once per failure.
 * Mutations with bespoke error UX set `meta.suppressGlobalMutationError`.
 */
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (mutation.meta?.suppressGlobalMutationError) {
      return;
    }

    const classified = captureClassifiedException(error, {
      source: 'async',
      layer: 'action',
      expectedCodeErrors: mutation.meta?.expectedCodeErrors,
    });

    showUnexpectedMutationErrorToast(classified);
  },
});

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: queryConfig,
});

export type QueryConfig<FetcherFnType extends (...args: any) => any> = UseQueryOptions<
  Awaited<ReturnType<FetcherFnType>>
>;

export type MutationConfig<FetcherFnType extends (...args: any) => any> = UseMutationOptions<
  Awaited<ReturnType<FetcherFnType>>,
  AxiosError,
  Parameters<FetcherFnType>
>;
